// src/App.jsx
import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import "./App.css"; // Global styles

import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import InitialRedirect from "./components/InitialRedirect";
import Header from "./components/Header";
import Footer from "./components/Footer";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import ProtectedRoute from "./components/PrivateRoute";
import GoogleProvider from "./providers/GoogleOAuthProvider";

// Keep essential components as regular imports for better initial load
// Public pages - Convert to lazy loading
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Signup = lazy(() => import("./pages/Signup"));
const Blogs = lazy(() => import("./pages/Blog"));
const OtpVerify = lazy(() => import("./pages/OtpVerify"));
const Login = lazy(() => import("./pages/Login"));

const RequestReset = lazy(() => import("./pages/RequestReset"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// User pages - Convert to lazy loading
const UserLayout = lazy(() => import("./pages/user/UserLayout"));
const UserHome = lazy(() => import("./pages/user/UserHome"));
const ProfileBuilder = lazy(() => import("./pages/user/ProfileBuilder"));
const DocumentUpload = lazy(() => import("./pages/user/DocumentUpload"));
const SATprep = lazy(() => import("./pages/user/SATprep"));
const Scholarships = lazy(() => import("./pages/user/Scholarships"));
const CountryGuides = lazy(() => import("./pages/user/CountryGuides"));
const UniversitySearch = lazy(() => import("./pages/user/UniversitySearch"));
const VisaDocuments = lazy(() => import("./pages/user/VisaDocuments"));
const ApplicationTracker = lazy(() =>
  import("./pages/user/ApplicationTracker")
);
const VisaInterview = lazy(() => import("./pages/user/VisaInterview"));
const CounselorBooking = lazy(() => import("./pages/user/CounselorBooking"));
const UserChat = lazy(() => import("./pages/user/UserChat"));
const TravelGuide = lazy(() => import("./pages/user/TravelGuide"));
const Accommodation = lazy(() => import("./pages/user/Accommodation"));
const Payments = lazy(() => import("./pages/user/Payments"));
const FAQ = lazy(() => import("./pages/user/FAQ"));
const UserNotification = lazy(() => import("./pages/user/UserNotification"));
const UserSettings = lazy(() => import("./pages/user/UserSettings"));

// Admin pages - Convert to lazy loading
const AdminRoute = lazy(() => import("./routes/AdminRoute"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UsersAdmin = lazy(() => import("./pages/admin/UsersAdmin"));
const VisaDocumentsAdmin = lazy(() =>
  import("./pages/admin/VisaDocumentsAdmin")
);
const ApplicationsAdmin = lazy(() => import("./pages/admin/ApplicationsAdmin"));
const VisaInterviewAdmin = lazy(() =>
  import("./pages/admin/VisaInterviewAdmin")
);
const CounselorBookingAdmin = lazy(() =>
  import("./pages/admin/CounselorBookingAdmin")
);
const ScholarshipsAdmin = lazy(() => import("./pages/admin/ScholarshipsAdmin"));
const CountryGuidesAdmin = lazy(() =>
  import("./pages/admin/CountryGuidesAdmin")
);
const UniversitySearchAdmin = lazy(() =>
  import("./pages/admin/UniversitySearchAdmin")
);
const TravelGuideAdmin = lazy(() => import("./pages/admin/TravelGuideAdmin"));
const AccommodationAdmin = lazy(() =>
  import("./pages/admin/AccommodationAdmin")
);
const PaymentsAdmin = lazy(() => import("./pages/admin/PaymentsAdmin"));
const FAQAdmin = lazy(() => import("./pages/admin/FAQAdmin"));
const NotificationAdmin = lazy(() => import("./pages/admin/NotificationAdmin"));
const SettingsAdmin = lazy(() => import("./pages/admin/SettingsAdmin"));
const DocumentAdmin = lazy(() => import("./pages/admin/DocumentAdmin"));
const SATprepAdmin = lazy(() => import("./pages/admin/SATprepAdmin"));
const ChatAdmin = lazy(() => import("./pages/admin/AdminChat"));
const BlogsAdmin = lazy(() => import("./pages/admin/BlogsAdmin"));

// Axios defaults
axios.defaults.withCredentials = true;
axios.defaults.baseURL = "http://localhost:5000/api";

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  </div>
);

// Component to conditionally render Header and Footer
const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isPublicRoute =
    !location.pathname.startsWith("/user") &&
    !location.pathname.startsWith("/admin");

  return (
    <div className="app-container">
      {isPublicRoute && <Header />}
      <main className={`min-h-[80vh] ${isPublicRoute ? "pt-16" : ""}`}>
        {children}
      </main>
      {isPublicRoute && <Footer />}
    </div>
  );
};

const App = () => {
  return (
    <GoogleProvider>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <InitialRedirect />
            <LayoutWrapper>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/blogs" element={<Blogs />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/verify-otp" element={<OtpVerify />} />
                  <Route path="/request-reset" element={<RequestReset />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<Terms />} />

                  {/* Protected User Dashboard - All routes start with /user/ */}
                  <Route
                    path="/user/*"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<LoadingSpinner />}>
                          <UserLayout />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to="dashboard" replace />}
                    />
                    <Route
                      path="dashboard"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <UserHome />
                        </Suspense>
                      }
                    />
                    {/* Profile Tools */}
                    <Route
                      path="profile-builder"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <ProfileBuilder />
                        </Suspense>
                      }
                    />
                    <Route
                      path="documentsUpload"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <DocumentUpload />
                        </Suspense>
                      }
                    />
                    {/* Test Preparation */}
                    <Route
                      path="sat-prep/*"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <SATprep />
                        </Suspense>
                      }
                    />
                    {/* Universities Guide */}
                    <Route
                      path="scholarships"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <Scholarships />
                        </Suspense>
                      }
                    />
                    <Route
                      path="country-guide"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <CountryGuides />
                        </Suspense>
                      }
                    />
                    <Route
                      path="university-search"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <UniversitySearch />
                        </Suspense>
                      }
                    />
                    {/* Visa & Documents */}
                    <Route
                      path="visa"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <VisaDocuments />
                        </Suspense>
                      }
                    />
                    <Route
                      path="application-tracker"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <ApplicationTracker />
                        </Suspense>
                      }
                    />
                    <Route
                      path="interview"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <VisaInterview />
                        </Suspense>
                      }
                    />
                    {/* Independent Tabs */}
                    <Route
                      path="counselor-booking"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <CounselorBooking />
                        </Suspense>
                      }
                    />
                    <Route
                      path="chat"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <UserChat />
                        </Suspense>
                      }
                    />
                    {/* Travels */}
                    <Route
                      path="travel-guide"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <TravelGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="accommodation"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <Accommodation />
                        </Suspense>
                      }
                    />
                    {/* More Independent Tabs */}
                    <Route
                      path="payments"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <Payments />
                        </Suspense>
                      }
                    />
                    <Route
                      path="faq"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <FAQ />
                        </Suspense>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <UserNotification />
                        </Suspense>
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <UserSettings />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* Admin Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AdminRoute />
                      </Suspense>
                    }
                  >
                    <Route
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <AdminLayout />
                        </Suspense>
                      }
                    >
                      {/* Default redirect to dashboard */}
                      <Route
                        index
                        element={<Navigate to="dashboard" replace />}
                      />

                      {/* Admin pages */}
                      <Route
                        path="dashboard"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <AdminDashboard />
                          </Suspense>
                        }
                      />
                      <Route
                        path="blogs"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <BlogsAdmin />
                          </Suspense>
                        }
                      />

                      <Route
                        path="users/profile-info"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <UsersAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="users/documents"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <DocumentAdmin />
                          </Suspense>
                        }
                      />

                      <Route
                        path="tests/sat-prep"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <SATprepAdmin />
                          </Suspense>
                        }
                      />

                      <Route
                        path="visa/documents"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <VisaDocumentsAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="visa/status"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <ApplicationsAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="visa/interview"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <VisaInterviewAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="counseling/booking"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <CounselorBookingAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="chat"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <ChatAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="universities/scholarships"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <ScholarshipsAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="universities/country-guide"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <CountryGuidesAdmin />
                          </Suspense>
                        }
                      />

                      <Route
                        path="universities/search"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <UniversitySearchAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="travels/guide"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <TravelGuideAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="travels/accommodation"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <AccommodationAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="payments"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <PaymentsAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="faq"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <FAQAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="notifications"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <NotificationAdmin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <SettingsAdmin />
                          </Suspense>
                        }
                      />
                    </Route>
                  </Route>

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </LayoutWrapper>
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </GoogleProvider>
  );
};

export default App;
